import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building,
  ClipboardList,
  FileText,
  Receipt,
  FileSignature,
  Package,
  Landmark,
  Settings,
  ChevronRight,
  LogOut,
  Search,
  Wallet,
  Shield,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { useFeatures } from "@/lib/useFeatures";
import { resetCrmEntered } from "@/lib/platformEntry";

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  soon?: boolean;
  /** nur sichtbar, wenn das optionale Modul aktiv ist */
  feature?: "expenses";
}

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: "",
    items: [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }],
  },
  {
    title: "CRM",
    items: [
      { to: "/kunden", icon: Users, label: "Kunden" },
      { to: "/objekte", icon: Building, label: "Objekte" },
      { to: "/auftraege", icon: ClipboardList, label: "Aufträge" },
    ],
  },
  {
    title: "Verkauf",
    items: [
      { to: "/angebote", icon: FileText, label: "Angebote" },
      { to: "/rechnungen", icon: Receipt, label: "Rechnungen" },
      { to: "/vertraege", icon: FileSignature, label: "Verträge" },
    ],
  },
  {
    title: "Stammdaten",
    items: [{ to: "/artikel", icon: Package, label: "Artikel" }],
  },
  {
    title: "Buchhaltung",
    items: [
      { to: "/steuer", icon: Landmark, label: "Steuer & Export" },
      { to: "/ausgaben", icon: Wallet, label: "Ausgaben", feature: "expenses" },
    ],
  },
];

const ROLE_LABEL: Record<string, string> = {
  owner: "Inhaber",
  admin: "Administrator",
  office: "Büro",
  field: "Monteur",
};

function initials(source: string): string {
  const parts = source
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  return (parts[0] ?? source).slice(0, 2).toUpperCase();
}

function Item({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  if (item.soon) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sidebar-fg-muted text-sm opacity-40 cursor-not-allowed">
        <item.icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1">{item.label}</span>
        <span className="text-2xs uppercase tracking-wide">bald</span>
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      className={({ isActive }) => cn("nav-item", isActive && "active")}
    >
      {({ isActive }) => (
        <>
          <item.icon
            className={cn(
              "w-4 h-4 flex-shrink-0",
              isActive ? "text-white" : "text-sidebar-fg-muted",
            )}
          />
          <span className="flex-1">{item.label}</span>
          {isActive && <ChevronRight className="w-3 h-3 text-white/50" />}
        </>
      )}
    </NavLink>
  );
}

const isMac =
  typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

export function Sidebar({
  open = false,
  onClose,
}: {
  open?: boolean;
  onClose?: () => void;
}) {
  const utils = trpc.useUtils();
  const setQuickSwitcherOpen = useAppStore((s) => s.setQuickSwitcherOpen);
  const { expensesVisible } = useFeatures();
  const branding = trpc.branding.get.useQuery(undefined, { retry: false });
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  const platformAccess = trpc.platform.access.useQuery(undefined, { retry: false });
  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      resetCrmEntered();
      utils.auth.me.invalidate();
    },
  });

  const name = branding.data?.organization?.legalName ?? "Mavatech";
  const hasLogo = !!branding.data?.logoUrl;
  const userName = me.data?.user.name || me.data?.user.email || "—";
  const roleLabel = me.data?.role
    ? (ROLE_LABEL[me.data.role] ?? me.data.role)
    : "";

  return (
    <>
      {/* Abdunklung hinter dem ausgeklappten Menü — nur mobil, schließt beim Antippen. */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "w-60 flex-shrink-0 bg-sidebar border-r border-sidebar-line flex flex-col",
          "fixed inset-y-0 left-0 z-40 transition-transform duration-200 md:static md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="md:hidden absolute top-3 right-3 text-sidebar-fg-muted hover:text-sidebar-fg p-1"
          aria-label="Menü schließen"
        >
          <X className="w-5 h-5" />
        </button>
        {hasLogo ? (
          // Mit Logo: heller Träger, damit das Logo auf der dunklen Leiste klar lesbar bleibt.
          <div className="p-3 flex-shrink-0 border-b border-sidebar-line">
            <div className="rounded-lg bg-white px-3 py-2.5">
              <img
                src={branding.data!.logoUrl!}
                alt={name}
                className="block w-full h-auto max-h-16 object-contain"
              />
            </div>
          </div>
        ) : (
          <div className="h-14 flex items-center px-4 flex-shrink-0 border-b border-sidebar-line">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-md bg-accent flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold leading-none">
                  M
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-sidebar-fg leading-none truncate">
                  {name}
                </p>
                <p className="text-2xs text-sidebar-fg-muted leading-none mt-1">
                  CRM
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="px-3 pt-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              setQuickSwitcherOpen(true);
              onClose?.();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sidebar-fg-muted hover:text-sidebar-fg hover:bg-white/5 text-sm transition-colors"
          >
            <Search className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 text-left">Suchen</span>
            <kbd className="text-2xs border border-sidebar-line rounded px-1.5 py-0.5">
              {isMac ? "⌘K" : "Strg+K"}
            </kbd>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {SECTIONS.map((section) => (
            <div key={section.title || "main"} className="mb-4 last:mb-0">
              {section.title && (
                <p className="px-3 pb-1.5 text-2xs font-semibold text-sidebar-fg-muted uppercase tracking-widest">
                  {section.title}
                </p>
              )}
              {section.items
                .filter((item) => item.feature !== "expenses" || expensesVisible)
                .map((item) => (
                <Item key={item.to} item={item} onNavigate={onClose} />
              ))}
            </div>
          ))}
        </nav>

        <div className="px-3 pb-3 pt-3 border-t border-sidebar-line flex-shrink-0 space-y-1">
          {platformAccess.data?.allowed && (
            <Item
              item={{ to: "/plattform", icon: Shield, label: "Plattform-Verwaltung" }}
              onNavigate={onClose}
            />
          )}
          <NavLink
            to="/einstellungen"
            onClick={onClose}
            className={({ isActive }) => cn("nav-item", isActive && "active")}
          >
            {({ isActive }) => (
              <>
                <Settings
                  className={cn(
                    "w-4 h-4 flex-shrink-0",
                    isActive ? "text-white" : "text-sidebar-fg-muted",
                  )}
                />
                <span className="flex-1">Einstellungen</span>
              </>
            )}
          </NavLink>

          <div className="flex items-center gap-2.5 px-2 py-2 rounded-md">
            <div className="w-8 h-8 rounded-full bg-white/10 text-sidebar-fg flex items-center justify-center flex-shrink-0 text-xs font-semibold">
              {initials(userName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-sidebar-fg leading-tight truncate">
                {userName}
              </p>
              <p className="text-2xs text-sidebar-fg-muted leading-tight truncate">
                {roleLabel}
              </p>
            </div>
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              title="Abmelden"
              className="text-sidebar-fg-muted hover:text-sidebar-fg transition-colors p-1 flex-shrink-0"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
